import LogRocket from 'lib/logrocket'

export interface ResponseError {
  ok: false
  error: Error
  problem: string

  data?: unknown
  status?: number
}

/**
 * Type guard for checking if something is a response error.
 */
export function isResponseError(e: unknown): e is ResponseError {
  if (e == null) return false
  return (
    (e as ResponseError).ok === false &&
    (e as ResponseError).error instanceof Error
  )
}

export interface ResponseOk<T> {
  ok: true
  data: T
  status: number
}

export type SafeResponse<T> = ResponseOk<T> | ResponseError

export const FETCH_ERROR = 'FETCH_ERROR'
export const NONE = 'NONE'
export const CLIENT_ERROR = 'CLIENT_ERROR'
export const SERVER_ERROR = 'SERVER_ERROR'
// const TIMEOUT_ERROR = 'TIMEOUT_ERROR'
// const CONNECTION_ERROR = 'CONNECTION_ERROR'
// const NETWORK_ERROR = 'NETWORK_ERROR'
const UNKNOWN_ERROR = 'UNKNOWN_ERROR'

// const TIMEOUT_ERROR_CODES = ['ECONNABORTED']
// const NODEJS_CONNECTION_ERROR_CODES = ['ENOTFOUND', 'ECONNREFUSED', 'ECONNRESET']

const defaultHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json;charset=UTF-8'
}

function getProblemFromResponse(res: Response): string {
  if (res.status >= 500) return SERVER_ERROR
  if (res.status >= 400 && res.status <= 499) return CLIENT_ERROR
  return UNKNOWN_ERROR
}

const defaultErrorMessage = 'Error while fetching data.'
async function parseErrorMessageFromResponse(data: any): Promise<string> {
  try {
    return data == null
      ? defaultErrorMessage
      : typeof data === 'string'
      ? data
      : typeof data === 'object' &&
        Object.prototype.hasOwnProperty.call(data, 'message')
      ? data.message
      : JSON.stringify(data, null, '\t')
  } catch (e) {
    return defaultErrorMessage
  }
}

const toArrayBuffer = (res: Response) => res.arrayBuffer()
const toText = (res: Response) => res.text()
export const toJSON = (res: Response) => res.json()

export function fetchText(url: string, options?: RequestInit) {
  return safeFetch<string>(url, toText, options)
}
export function fetchData<T>(url: string, options?: RequestInit) {
  return safeFetch<T>(url, toJSON, {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options?.headers
    }
  })
}
export function fetchBuffer(url: string, options?: RequestInit) {
  return safeFetch<ArrayBuffer>(url, toArrayBuffer, options)
}

/**
 * Never throw errors. Always return a response.
 */
export async function safeFetch<T>(
  url: string,
  parseData: (response: Response) => Promise<T>,
  options?: RequestInit
): Promise<SafeResponse<T>> {
  try {
    const response = await fetch(url, options)
    if (response.ok) {
      return {
        data: await parseData(response),
        ok: true,
        status: response.status
      }
    } else {
      const data = await (response.headers.get('Content-Type')?.includes('json')
        ? response.json()
        : response.text())
      return {
        data,
        error: new Error(await parseErrorMessageFromResponse(data)),
        ok: false,
        problem: getProblemFromResponse(response),
        status: response.status
      }
    }
  } catch (e: unknown) {
    const error = e instanceof Error ? e : new Error(JSON.stringify(e))
    LogRocket.captureException(error, {
      extras: {
        fetchUrl: url
      },
      tags: {
        type: 'fetch'
      }
    })
    return {
      error,
      ok: false,
      problem: FETCH_ERROR
    }
  }
}

/**
 * Throw the response when using SWR.
 */
export async function swrFetcher<T = unknown>(opts: string | [string]) {
  const url = typeof opts === 'string' ? opts : opts[0]
  const res = await fetchData<T>(url)
  if (res.ok) return res.data
  else throw res
}

/**
 * Safe DELETE
 */
export function safeDelete(url: string) {
  return safeFetch<null>(url, toJSON, {
    method: 'DELETE'
  })
}

/**
 * Simple GET
 */
export function getJSON<T>(url: string) {
  return safeFetch<T>(url, toJSON, {headers: defaultHeaders})
}

/**
 * Simple POST
 */
export function postJSON<T, R = T>(url: string, json: T) {
  return safeFetch<R>(url, toJSON, {
    body: JSON.stringify(json),
    headers: defaultHeaders,
    method: 'POST'
  })
}

/**
 * Simple PUT
 */
export function putJSON<T>(url: string, json: Partial<T>) {
  return safeFetch<T>(url, toJSON, {
    body: JSON.stringify(json),
    headers: defaultHeaders,
    method: 'PUT'
  })
}
